import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ClearReconciliationDataModal from './customForms/ClearReconciliationDataModal'
import UnConsumeQuoteModal from './customForms/UnConsumeQuoteModal'
import { ReceiptOutlined, Warning } from '@mui/icons-material'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { Helmet } from 'react-helmet'
import { IStores } from 'src/Stores'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import AssignPendModal from './modals/shared/AssignPendModal'
import ActionLogModal from './modals/shared/ActionLogModal'
import StandardCommentModal from './modals/shared/StandardCommentModal'
import AssociatePatientEOCModal from './customForms/AssociatePatientEOCModal'
import { ICMS1500, IUser } from 'src/Definitions'
import { ProcessAction } from 'src/viewModels/workflow/ProcessAction'
import {
  GetClaimReadyForBilloutInbox,
  processAction,
  processDynamicAction,
} from 'src/services/WorkflowService'
import { StaticAction } from 'src/utils/StaticAction'
import { CommentOutline } from 'mdi-material-ui'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { WorkflowInboxItemHeader } from 'src/viewModels/workflow/InboxItemHeader'
import { DocumentCustomForm } from 'src/utils/DocumentCustomForm'
import { ClaimCustomForm } from 'src/utils/ClaimCustomForm'
import { InvoiceCustomForm } from 'src/utils/InvoiceCustomForm'
import { InboxItemType, InboxTypeItemPathConstants } from 'src/utils/InboxItemType'
import { ClaimStaticAction } from 'src/utils/ClaimStaticAction.enum'
import { BilloutStaticAction } from 'src/utils/BilloutStaticAction.enum'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import CompleteWithContractTBDModal from './customForms/CompleteWithContractTBDModal'
import CompleteClaimModal from './customForms/CompleteClaimModal'
import MarkClaimAsAppealModal from './customForms/MarkClaimAsAppealModal'
import SendToAdjusterModal from 'src/workflow/customForms/SendToAdjusterModal'
import { QuoteCustomForm } from 'src/utils/QuoteCustomForm'
import AssignToEntryUserAndSendModal from './modals/shared/AssignToEntryUserAndSendModal'
import { QuoteStaticAction } from 'src/utils/QuoteStaticAction.enum'
import { IQuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import SetBundleDurationDaysModal from './customForms/SetBundleDurationDaysModal'
import ReceiveInvoicePaymentModal from './customForms/ReceiveInvoicePaymentModal'
import CreateBilloutModal from './customForms/CreateBilloutModal'
import { BillOutCustomForm } from 'src/utils/BillOutCustomForm'
import { BillOutInboxItem } from 'src/viewModels/workflow/BillOutInboxItem'
import ReceiveBilloutPaymentModal from './customForms/ReceiveBilloutPaymentModal'
import ProcessRefundModal from './customForms/ProcessRefundModal'
import CreateAdditionalQuoteModal from './customForms/CreateAdditionalQuoteModal'
import { CaseCustomForm } from '../utils/CaseCustomForm'
import CompleteChronoRequestModal from './customForms/CompleteChronoRequestModal'
import { ChronoCompleteRequest } from 'src/viewModels/workflow/ChronoCompleteRequest'
import RecordsReceivedRequestModal from './customForms/RecordsReceivedRequestModal'
import { RecordsReceivedRequest } from 'src/viewModels/workflow/RecordsReceivedRequest'
import FinalizedRequestModal from './customForms/FinalizedRequestModal'
import { FinalizedRequest } from 'src/viewModels/workflow/FinalizedRequest'
import BackFromTranscriptionRequestModal from './customForms/BackFromTranscriptionRequestModal'
import { BackFromTranscriptionRequest } from 'src/viewModels/workflow/BackFromTranscriptionRequest'
import ReportSentRequestModal from './customForms/ReportSentRequestModal'
import { ReportSentRequest } from 'src/viewModels/workflow/ReportSentRequest'
import DictatedRequestModal from './customForms/DictatedRequestModal'
import { DictatedRequest } from 'src/viewModels/workflow/DictatedRequest'
import RecordsReviewedRequestModal from './customForms/RecordsReviewedRequestModal'
import { RecordsReviewedRequest } from 'src/viewModels/workflow/RecordsReviewedRequest'
import SendToOcrModal from './customForms/SendToOcrModal'
import { PatientInvoiceDetailDTO } from 'src/viewModels/workflow/PatientInvoiceDetailDTO'
import CreateCaseDetailItemModal from './customForms/CreateCaseDetailLineItemModal'
import EditPatientModal from './customForms/EditPatientModal'
import { MedicalPatientDTO } from 'src/viewModels/workflow/MedicalPatientDTO'
import EditCaseDetailLineItem from './customForms/EditCaseDetailLineItemModal'
import ApplyPaymentModal from './customForms/ApplyPaymentModal'
import { PaymentRequest } from 'src/viewModels/PaymentRequest'
import ProviderOverpaidAppealCompleteClaimModal from './customForms/ProviderOverpaidAppealCompleteClaimModal'
import { Autocomplete } from '@mui/material'
import { InvoiceStaticAction } from 'src/utils/InvoiceStaticAction.enum'
import SelectDefaultCaseLineItemModal from './customForms/SelectDefaultCaseLineItemModal'
import { SelectDefaultCaseLineItemRequest } from 'src/viewModels/workflow/SelectDefaultCaseLineItemRequest'
import UpdateInvoiceSentDateModal from './customForms/UpdateInvoiceSentDateModal'
import SupplementalDocumentationModal from './customForms/SupplementalDocumentationModal'

interface IWorkflowHeaderProps {
  headerInfo: WorkflowInboxItemHeader
  inboxType: InboxItemType
  icon: JSX.Element
  openActionLog?: (itemId: string) => void
  user?: IUser
  rerouteToPath?: (path: string) => void
  getClaimValuesForStaticAction?: () => Promise<ICMS1500>
  getQuoteValuesForStaticAction?: () => Promise<IQuoteDTO>
  getCaseChronoValuesForStaticAction?: () => Promise<ChronoCompleteRequest>
  getCaseRecordsReceivedValuesForStaticAction?: () => Promise<RecordsReceivedRequest>
  getCaseFinalizedValuesForStaticAction?: () => Promise<FinalizedRequest>
  getCaseBackFromTranscriptionValuesForStaticAction?: () => Promise<BackFromTranscriptionRequest>
  getCaseReportSentValuesForStaticAction?: () => Promise<ReportSentRequest>
  getCaseDictatedValuesForStaticAction?: () => Promise<DictatedRequest>
  getCaseRecordsReviewedValuesForStaticAction?: () => Promise<RecordsReviewedRequest>
  getCaseDetailLineItemValuesForStaticAction?: () => Promise<PatientInvoiceDetailDTO>
  getCasePatientDetailsForStaticAction?: () => Promise<MedicalPatientDTO>
}

type ModalRenderProps = {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  claimEpisodeOfCareId?: string
  inboxId: string
  associatedItemId: string
  updateClaim: boolean
  action: InboxActionSummary
  isDynamicItem: boolean
  inboxType: InboxItemType
  data: ObjectiveProcessAction
  actionList: InboxActionSummary[]
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  closeTab?: () => void
  entryUser: string
  nextInboxId?: number
  nextInboxText?: string
  takeoverOfCareDate?: Date
  userName?: string
}
type ModalName =
  | 'assignPendModal'
  | 'commentModal'
  | 'associatePatientModal'
  | 'actionLogModal'
  | 'completeTBDModal'
  | 'completeClaimModal'
  | 'markClaimAsAppealModal'
  | 'sendToAdjusterModal'
  | 'assignToEntryUserAndSendModal'
  | 'setBundleDurationDaysModal'
  | 'receiveInvoicePaymentModal'
  | 'createBilloutModal'
  | 'receiveBilloutPaymentModal'
  | 'processRefundModal'
  | 'createAdditionalQuoteModal'
  | 'completeChronoRequestModal'
  | 'recordsReceivedRequestModal'
  | 'finalizedRequestModal'
  | 'backFromTranscriptionRequestModal'
  | 'reportSentRequestModal'
  | 'dictatedRequestModal'
  | 'recordsReviewedRequestModal'
  | 'sendToOcrModal'
  | 'createCaseDetailLineItemModal'
  | 'editPatientModal'
  | 'editCaseDetailLineItemModal'
  | 'applyPaymentModal'
  | 'providerOverpaidAppealCompleteClaimModal'
  | 'selectDefaultCaseLineItemModal'
  | 'updateInvoiceSentDateModal'
  | 'clearReconciliationDataModal'
  | 'unconsumeQuoteModal'
  | 'supplementalDocumentationModal'

const modalRenderers: Record<
  ModalName,
  (props: ModalRenderProps) => React.ReactElement | null
> = {
  assignPendModal: (props) => <AssignPendModal {...props} />,
  commentModal: (props) => <StandardCommentModal {...props} />,
  sendToOcrModal: (props) => <SendToOcrModal {...props} />,
  associatePatientModal: (props) => <AssociatePatientEOCModal {...props} />,
  actionLogModal: (props) => <ActionLogModal {...props} />,
  completeTBDModal: (props) => <CompleteWithContractTBDModal {...props} />,
  completeClaimModal: (props) => <CompleteClaimModal {...props} />,
  markClaimAsAppealModal: (props) => <MarkClaimAsAppealModal {...props} />,
  sendToAdjusterModal: (props) => <SendToAdjusterModal {...props} />,
  assignToEntryUserAndSendModal: (props) => <AssignToEntryUserAndSendModal {...props} />,
  setBundleDurationDaysModal: (props) => <SetBundleDurationDaysModal {...props} />,
  receiveInvoicePaymentModal: (props) => <ReceiveInvoicePaymentModal {...props} />,
  createBilloutModal: (props) => <CreateBilloutModal {...props} />,
  receiveBilloutPaymentModal: (props) => <ReceiveBilloutPaymentModal {...props} />,
  processRefundModal: (props) => <ProcessRefundModal {...props} />,
  createAdditionalQuoteModal: (props) => <CreateAdditionalQuoteModal {...props} />,
  completeChronoRequestModal: (props) => <CompleteChronoRequestModal {...props} />,
  recordsReceivedRequestModal: (props) => <RecordsReceivedRequestModal {...props} />,
  finalizedRequestModal: (props) => <FinalizedRequestModal {...props} />,
  backFromTranscriptionRequestModal: (props) => (
    <BackFromTranscriptionRequestModal {...props} />
  ),
  reportSentRequestModal: (props) => <ReportSentRequestModal {...props} />,
  dictatedRequestModal: (props) => <DictatedRequestModal {...props} />,
  recordsReviewedRequestModal: (props) => <RecordsReviewedRequestModal {...props} />,
  createCaseDetailLineItemModal: (props) => <CreateCaseDetailItemModal {...props} />,
  editPatientModal: (props) => <EditPatientModal {...props} />,
  editCaseDetailLineItemModal: (props) => <EditCaseDetailLineItem {...props} />,
  applyPaymentModal: (props) => <ApplyPaymentModal {...props} />,
  providerOverpaidAppealCompleteClaimModal: (props) => (
    <ProviderOverpaidAppealCompleteClaimModal {...props} />
  ),
  selectDefaultCaseLineItemModal: (props) => (
    <SelectDefaultCaseLineItemModal {...props} />
  ),
  updateInvoiceSentDateModal: (props) => <UpdateInvoiceSentDateModal {...props} />,
  clearReconciliationDataModal: (props) => <ClearReconciliationDataModal {...props} />,
  unconsumeQuoteModal: (props) => <UnConsumeQuoteModal {...props} />,
  supplementalDocumentationModal: (props) => (
    <SupplementalDocumentationModal {...props} />
  ),
}

interface IWorkflowHeaderState {
  actionToExecute?: InboxActionSummary
  selectedAction?: InboxActionSummary
  activeModal: ModalName | null
  executed: boolean
  data: ObjectiveProcessAction
  billoutSearchDefault?: number | undefined
}

class WorkflowHeader extends React.Component<IWorkflowHeaderProps> {
  public state: IWorkflowHeaderState = {
    activeModal: null,
    actionToExecute: undefined,
    selectedAction: undefined,
    executed: false,
    data: undefined as unknown as ObjectiveProcessAction,
    billoutSearchDefault: undefined,
  }

  private priorityNames = {
    1: 'Normal',
    2: 'Medium',
    3: 'High',
  }

  componentDidMount(): void {
    this.closeModal = this.closeModal.bind(this)
    this.closeTab = this.closeTab.bind(this)
    this.handleProcessActionFinish = this.handleProcessActionFinish.bind(this)
  }

  public closeModal() {
    this.setState({ activeModal: null, executed: false })
  }

  public closeTab() {
    if (
      window.location.toString().indexOf('default-inbox') > -1 ||
      window.location.toString().indexOf('default-billout-search') > -1 ||
      window.location.toString().indexOf('received-search-default') > -1 ||
      window.location.toString().indexOf('process-refunds-search') > -1
    ) {
      window.close()
    } else if (window.location.toString().indexOf('billout-search') > -1) {
      this.props.rerouteToPath!(`/billOut`)
    } else {
      this.props.rerouteToPath!(`/workflow-inboxes`)
    }
  }

  private handleProcessActionFinish(selectedAction?: InboxActionSummary) {
    if (selectedAction?.closeAfterProcessing) {
      this.closeTab()
    } else if (
      !selectedAction?.closeAfterProcessing &&
      selectedAction?.nextInboxId == undefined &&
      this.state.billoutSearchDefault == undefined &&
      !selectedAction?.outOfWorkflow
    ) {
      this.props.rerouteToPath!(
        `/workflow/${InboxTypeItemPathConstants[this.props.inboxType]}/${
          this.props.headerInfo.associatedItemId
        }/default-inbox`
      )
      window.location.reload()
    } else if (this.state.billoutSearchDefault != undefined) {
      var claimId = (this.props.headerInfo as unknown as BillOutInboxItem).claimId
      this.props.rerouteToPath!(
        `/workflow/billout/${claimId}/default-billout-search/${this.state.billoutSearchDefault}`
      )
    } else {
      window.location.reload()
    }
  }

  private async executeAction() {
    var selectedAction = this.state.selectedAction as unknown as InboxActionSummary

    if (
      (selectedAction.staticActionId == BilloutStaticAction.REVERSE_DISMISSAL ||
        selectedAction.staticActionId == BilloutStaticAction.REVERSE_BILLOUT) &&
      !selectedAction.closeAfterProcessing &&
      window.location.toString().indexOf('default-inbox') > -1
    ) {
      this.setState({
        billoutSearchDefault: await GetClaimReadyForBilloutInbox(),
      })
    }
    if (selectedAction.hasCustomForm) {
      switch (selectedAction.customFormName) {
        case QuoteCustomForm.ClearReconciliationData:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'clearReconciliationDataModal',
          })
          break
        case QuoteCustomForm.UnConsumeQuote:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'unconsumeQuoteModal',
          })
          break
        case DocumentCustomForm.CreateClaim:
        case ClaimCustomForm.UpdatePatient:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'associatePatientModal',
          })
          break
        case DocumentCustomForm.SupplementalDocumentation:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'supplementalDocumentationModal',
          })
          break
        case ClaimCustomForm.InitialComplete:
          this.state.data.claimData = await this.props.getClaimValuesForStaticAction!()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'completeTBDModal',
          })
          break
        case ClaimCustomForm.FinalComplete:
          this.state.data.claimData = await this.props.getClaimValuesForStaticAction!()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'completeClaimModal',
          })
          break
        case ClaimCustomForm.ProviderOverpaidAppealCompleteClaim:
          this.state.data.claimData = await this.props.getClaimValuesForStaticAction!()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'providerOverpaidAppealCompleteClaimModal',
          })
          break
        case ClaimCustomForm.SendToOcr:
          this.state.data.claimData = await this.props.getClaimValuesForStaticAction!()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'sendToOcrModal',
          })
          break
        case ClaimCustomForm.MarkAsAppeal:
          this.state.data.claimData = await this.props.getClaimValuesForStaticAction!()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'markClaimAsAppealModal',
          })
          break
        case QuoteCustomForm.SendToAdjuster:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'sendToAdjusterModal',
          })
          break
        case QuoteCustomForm.SetBundleDurationDays:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'setBundleDurationDaysModal',
          })
          break
        case QuoteCustomForm.CreateAdditionalQuote:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'createAdditionalQuoteModal',
          })
          break
        case CaseCustomForm.AssignToEntryUserAndSend:
        case ClaimCustomForm.AssignToEntryUserAndSend:
        case DocumentCustomForm.AssignToEntryUserAndSend:
        case QuoteCustomForm.AssignToEntryUserAndSend:
        case InvoiceCustomForm.AssignToEntryUserAndSend:
          this.state.data.assignUser = this.props.headerInfo.entryUser
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'assignToEntryUserAndSendModal',
            inboxType: this.props.inboxType,
          })
          break
        case QuoteCustomForm.ReceiveInvoicePayment:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'receiveInvoicePaymentModal',
          })
          break
        case BillOutCustomForm.CreateBillout:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'createBilloutModal',
          })
          break
        case BillOutCustomForm.ReceiveBilloutPayment:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'receiveBilloutPaymentModal',
          })
          break
        case ClaimCustomForm.ProcessRefund:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'processRefundModal',
          })
          break
        case CaseCustomForm.CompleteChrono:
          this.state.data.chronoCompleteRequest = new ChronoCompleteRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'completeChronoRequestModal',
          })
          break
        case CaseCustomForm.RecordsReceived:
          this.state.data.recordsReceivedRequest = new RecordsReceivedRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'recordsReceivedRequestModal',
          })
          break
        case CaseCustomForm.Finalized:
          this.state.data.finalizedRequest = new FinalizedRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'finalizedRequestModal',
          })
          break
        case CaseCustomForm.BackFromTranscription:
          this.state.data.backFromTranscriptionRequest =
            new BackFromTranscriptionRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'backFromTranscriptionRequestModal',
          })
          break
        case CaseCustomForm.ReportSent:
          this.state.data.reportSentRequest = new ReportSentRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'reportSentRequestModal',
          })
          break
        case CaseCustomForm.Dictated:
          this.state.data.dictatedRequest = new DictatedRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'dictatedRequestModal',
          })
          break
        case CaseCustomForm.RecordsReviewed:
          this.state.data.recordsReviewedRequest = new RecordsReviewedRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'recordsReviewedRequestModal',
          })
          break
        case CaseCustomForm.AddCaseDetailItem:
          this.state.data.lineDetailRequest = new PatientInvoiceDetailDTO()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'createCaseDetailLineItemModal',
          })
          break
        case CaseCustomForm.UpdateCasePatient:
          this.state.data.casePatientRequest = new MedicalPatientDTO()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'editPatientModal',
          })
          break
        case CaseCustomForm.UpdateCaseDetailItem:
          this.state.data.lineDetailRequest = new PatientInvoiceDetailDTO()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'editCaseDetailLineItemModal',
          })
          break
        case CaseCustomForm.ApplyReceivePayment:
          this.state.data.casePaymentRequest = new PaymentRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'applyPaymentModal',
          })
          break
        case CaseCustomForm.SelectDefaultCaseLineItem:
          this.state.data.selectDefaultCaseLineItemRequest =
            new SelectDefaultCaseLineItemRequest()
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'selectDefaultCaseLineItemModal',
          })
          break
        case InvoiceCustomForm.UpdateInvoiceSentDate:
          this.setState({
            data: this.state.data,
            actionToExecute: selectedAction,
            activeModal: 'updateInvoiceSentDateModal',
          })
          break
        default:
          break
      }
    } else if (
      selectedAction?.staticActionId != undefined &&
      selectedAction?.staticActionId != StaticAction.AUTO_ASSIGN &&
      selectedAction?.staticActionId != BilloutStaticAction.PRINT &&
      selectedAction?.staticActionId != BilloutStaticAction.PRINT_VERIFIED &&
      selectedAction?.staticActionId != BilloutStaticAction.REVERSE_BILLOUT &&
      selectedAction?.staticActionId != BilloutStaticAction.REVERSE_DISMISSAL &&
      selectedAction?.staticActionId != InvoiceStaticAction.INVOICE_SENT &&
      selectedAction?.staticActionId != QuoteStaticAction.UN_INVOICED_FROM_QUOTE
    ) {
      switch (selectedAction.staticActionId) {
        case StaticAction.RELEASE_LOCK:
          var data = new ProcessAction()
          processAction(
            data,
            selectedAction!.id.toString(),
            this.props.headerInfo.id.toString()
          )
            .then(() => {
              this.handleProcessActionFinish(selectedAction)
            })
            .catch(() => {
              this.setState({ executed: false })
            })
          break
        case StaticAction.ASSIGN:
        case StaticAction.PEND:
          this.setState({
            actionToExecute: selectedAction,
            activeModal: 'assignPendModal',
          })
          break
        case ClaimStaticAction.SAVE_CLAIM:
          {
            let data = new ObjectiveProcessAction()
            data.claimData = await this.props.getClaimValuesForStaticAction!()

            if (selectedAction.displayComment) {
              this.setState({
                data: data,
                actionToExecute: selectedAction,
                activeModal: selectedAction.displayComment ? 'commentModal' : null,
              })
            } else {
              processAction(
                data,
                selectedAction!.id.toString(),
                this.props.headerInfo.id.toString()
              )
                .then(() => {
                  this.handleProcessActionFinish(selectedAction)
                })
                .catch(() => {
                  this.setState({ executed: false })
                })
            }
          }
          break
        case QuoteStaticAction.SAVE_QUOTE:
          {
            let data = new ObjectiveProcessAction()
            const getQuoteValuesForStaticAction = this.props.getQuoteValuesForStaticAction

            if (!getQuoteValuesForStaticAction) {
              throw new Error('Quote static action callback is not available')
            }

            data.quoteData = await getQuoteValuesForStaticAction()

            if (selectedAction.displayComment) {
              this.setState({
                data: data,
                actionToExecute: selectedAction,
                activeModal: selectedAction.displayComment ? 'commentModal' : null,
              })
            } else {
              processAction(
                data,
                selectedAction!.id.toString(),
                this.props.headerInfo.id.toString()
              )
                .then(() => {
                  this.handleProcessActionFinish(selectedAction)
                })
                .catch(() => {
                  this.setState({ executed: false })
                })
            }
          }
          break
        default:
          break
      }
    } else if (selectedAction.displayComment) {
      //Comment modal
      this.setState({
        actionToExecute: selectedAction,
        activeModal: 'commentModal',
      })
    } else {
      var data = new ProcessAction()

      if (this.props.headerInfo.isDynamicItem) {
        processDynamicAction(
          data,
          this.props.headerInfo.inboxId.toString(),
          selectedAction!.id.toString(),
          this.props.headerInfo.associatedItemId.toString()
        )
          .then(() => {
            this.handleProcessActionFinish(selectedAction)
          })
          .catch(() => {
            this.setState({ executed: false })
          })
      } else {
        processAction(
          data,
          selectedAction!.id.toString(),
          this.props.headerInfo.id.toString()
        )
          .then(() => {
            this.handleProcessActionFinish(selectedAction)
          })
          .catch(() => {
            this.setState({ executed: false })
          })
      }
    }
  }

  private close() {
    if (
      this.props.headerInfo.restrictedToOutOfWorkflowActions ||
      this.props.headerInfo.isDynamicItem
    ) {
      this.closeTab()
    } else {
      var actions = this.props.headerInfo.actions
      var unlock = actions.find((x) => x.staticActionId == StaticAction.RELEASE_LOCK)

      var data = new ProcessAction()
      data.assignUser = ''

      processAction(data, unlock!.id.toString(), this.props.headerInfo.id.toString())
        .then(() => {
          this.closeTab()
        })
        .catch(() => {
          this.setState({ executed: false })
        })
    }
  }

  public render() {
    const { headerInfo } = this.props

    return (
      <>
        {this.props.headerInfo && (
          <>
            <Helmet>
              <title>{'Process ' + this.props.headerInfo.inboxName}</title>
            </Helmet>
            {(this.state.activeModal == 'actionLogModal' ||
              (this.state.activeModal &&
                this.state.actionToExecute &&
                this.state.executed)) &&
              modalRenderers[this.state.activeModal]({
                closeModal: this.closeModal,
                isOpen: this.state.activeModal != undefined,
                inboxItemId: headerInfo.id,
                action:
                  this.state.activeModal == 'actionLogModal' ||
                  this.state.actionToExecute == undefined
                    ? new InboxActionSummary()
                    : this.state.actionToExecute,
                inboxId: headerInfo.inboxId.toString(),
                associatedItemId: headerInfo.associatedItemId,
                updateClaim: this.props.inboxType == InboxItemType.Claim,
                isDynamicItem: headerInfo.isDynamicItem,
                inboxType: this.props.inboxType,
                data: this.state.data,
                actionList: headerInfo.actions,
                handleProcessActionFinish: this.handleProcessActionFinish,
                closeTab: this.closeTab,
                entryUser: headerInfo.entryUser,
                nextInboxId: this.state.selectedAction?.nextInboxId,
                nextInboxText: this.state.selectedAction?.nextInboxText,
                userName: this.props.user?.name,
              })}

            <ResponsiveAppBar
              title={'Process ' + this.props.headerInfo.inboxName}
              pageIcon={this.props.icon}
            ></ResponsiveAppBar>
            {this.state.executed && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '2',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}

            <Grid
              container
              className="actionBar"
              style={{
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingTop: '20px',
              }}
            >
              <Grid item container direction="column" xs={9}>
                <Grid item container direction="row" justifyContent="flex-start">
                  <Grid item xs={5}>
                    <FormControl style={{ width: '100%' }} variant="outlined">
                      <Autocomplete
                        size="small"
                        id="actionSelect"
                        options={
                          headerInfo.actions?.filter(
                            (action) => !action.excludeFromMainActionList
                          ) || []
                        }
                        getOptionLabel={(option: InboxActionSummary) => option.actionName}
                        value={this.state.selectedAction || null}
                        onChange={(_, value: InboxActionSummary | null) => {
                          this.setState({
                            selectedAction: value || undefined,
                            actionToExecute: undefined,
                            data: new ObjectiveProcessAction(),
                          })
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Actions"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        )}
                        fullWidth
                        disableClearable={false}
                      />
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    container
                    xs={4}
                    spacing={1}
                    style={{
                      paddingLeft: '8px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <Grid item>
                      <Button
                        style={{ height: '40px' }}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          this.setState({ executed: true })
                          this.executeAction()
                        }}
                        disabled={
                          this.state.executed || this.state.selectedAction == undefined
                        }
                      >
                        Execute
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        style={{ height: '40px' }}
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          this.setState({ executed: true })
                          this.close()
                        }}
                        disabled={this.state.executed}
                      >
                        {headerInfo.restrictedToOutOfWorkflowActions ||
                        headerInfo.isDynamicItem
                          ? 'Close'
                          : 'Unlock'}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography variant="caption">
                    INBOX: <strong>{headerInfo.inboxName}</strong>
                  </Typography>
                </Grid>
                {headerInfo.lockedBy && (
                  <Grid item>
                    <Typography variant="caption" style={{ display: 'contents' }}>
                      LOCKED BY: <strong>{headerInfo.lockedBy}</strong>
                    </Typography>
                    &nbsp;&nbsp;
                    {this.props.headerInfo.lockedBy != this.props.user?.userName && (
                      <Tooltip title="This item is locked">
                        <Warning style={{ fontSize: '16px', color: '#ffc107' }} />
                      </Tooltip>
                    )}
                  </Grid>
                )}
                {headerInfo.lastAction && (
                  <Grid item>
                    <Typography variant="caption" style={{ display: 'contents' }}>
                      LAST ACTION:&nbsp;
                      <strong>{headerInfo.lastAction.name}</strong> on{' '}
                      {moment(headerInfo.lastAction.date).format('L')}
                      &nbsp;by&nbsp;{headerInfo.lastAction.user}
                    </Typography>
                    &nbsp;&nbsp;
                    {headerInfo.lastAction.comment && (
                      <Tooltip title={headerInfo.lastAction.comment}>
                        <span>
                          <CommentOutline />
                        </span>
                      </Tooltip>
                    )}
                  </Grid>
                )}
                {this.props.inboxType == InboxItemType.Billout &&
                  (this.props.headerInfo as unknown as BillOutInboxItem)
                    .currentStatus && (
                    <Grid item>
                      <Typography variant="caption" style={{ display: 'contents' }}>
                        BILL OUT STATUS:&nbsp;
                        <strong>
                          {
                            (this.props.headerInfo as unknown as BillOutInboxItem)
                              .currentStatus
                          }
                        </strong>
                      </Typography>
                    </Grid>
                  )}
              </Grid>
              <Grid
                container
                item
                direction="column"
                xs={3}
                alignContent="flex-end"
                style={{ paddingRight: '8px' }}
              >
                {headerInfo.entryDate && (
                  <Grid item>
                    <Typography variant="caption">
                      ENTRY DATE&nbsp;
                      <strong>
                        {moment(
                          headerInfo.isDynamicItem ? new Date() : headerInfo.entryDate
                        ).format('L')}
                      </strong>
                      &nbsp;
                      <Tooltip title="View Action Log">
                        <ReceiptOutlined
                          fontSize="small"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            this.setState({ activeModal: 'actionLogModal' })
                          }}
                        />
                      </Tooltip>
                    </Typography>
                  </Grid>
                )}
                {!headerInfo.isDynamicItem && headerInfo.priority && (
                  <Grid item>
                    <Typography variant="caption">
                      PRIORITY&nbsp;
                      <strong>{this.priorityNames[headerInfo.priority]}</strong>
                    </Typography>
                  </Grid>
                )}
                {headerInfo.pendDate && (
                  <Grid item>
                    <Typography variant="caption">
                      PEND&nbsp;
                      <strong>{moment(headerInfo.pendDate).format('L')}</strong>{' '}
                      <i>{headerInfo.pendNote}</i>
                    </Typography>
                  </Grid>
                )}
                {headerInfo.assignedUser && (
                  <Grid item>
                    <Typography variant="caption">
                      ASSIGNMENT: <strong>{headerInfo.assignedUser}</strong>
                      {headerInfo.assignmentDueDate && (
                        <i>
                          &nbsp; with due date&nbsp;
                          {moment(headerInfo.assignmentDueDate).format('MM/DD/YYYY')}
                        </i>
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </>
    )
  }
}

const InjectedWorkflowHeader = inject<
  IStores,
  IWorkflowHeaderProps,
  Partial<IWorkflowHeaderProps>,
  any
>((stores: IStores) => ({
  user: stores.global.user,
  rerouteToPath: stores.global.rerouteToPath,
  getClaimValuesForStaticAction: stores.cms1500.getClaimValuesForStaticAction,
  getQuoteValuesForStaticAction: stores.quotes.getQuoteValuesForStaticAction,
}))(WorkflowHeader)

export default InjectedWorkflowHeader
